import type { ReviewQueueItem } from "../domain/types";
import type { ReviewFilter } from "../review/queueStore";
import { reviewQueueStore } from "../review/queueStore";

const FILTERS: { id: ReviewFilter; label: string }[] = [
  { id: "pending_review", label: "Pending" },
  { id: "approved", label: "Approved" },
  { id: "rejected", label: "Rejected" },
  { id: "needs_resubmit", label: "Resubmit" },
  { id: "all", label: "All" },
];

function formatWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function ReviewList({
  items,
  filter,
  onFilter,
  selectedId,
  onSelect,
}: {
  items: ReviewQueueItem[];
  filter: ReviewFilter;
  onFilter: (f: ReviewFilter) => void;
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const counts = {
    pending_review: reviewQueueStore.listByStatus("pending_review").length,
    approved: reviewQueueStore.listByStatus("approved").length,
    rejected: reviewQueueStore.listByStatus("rejected").length,
    needs_resubmit: reviewQueueStore.listByStatus("needs_resubmit").length,
    all: reviewQueueStore.listAll().length,
  };

  return (
    <div className="rc-list">
      <header className="rc-list__header">
        <div>
          <div className="rc-eyebrow">Review console</div>
          <h1 className="rc-title">Verification queue</h1>
          <p className="rc-lead">
            Inspect ID, confirmation video, and photos. Approve or reject — status writes back to the
            queue store (ready for a future API).
          </p>
        </div>
      </header>

      <div className="rc-filters" role="tablist" aria-label="Queue filter">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            role="tab"
            aria-selected={filter === f.id}
            className={`rc-filter${filter === f.id ? " is-active" : ""}`}
            onClick={() => onFilter(f.id)}
          >
            {f.label}
            <span className="rc-filter__count">{counts[f.id]}</span>
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="rc-empty">
          <p>No cases in this filter.</p>
          <p className="rc-empty__hint">
            Complete onboarding verification, or load demo cases from the toolbar.
          </p>
        </div>
      ) : (
        <ul className="rc-case-list">
          {items.map((item) => (
            <li key={item.applicationId}>
              <button
                type="button"
                className={`rc-case${selectedId === item.applicationId ? " is-selected" : ""}`}
                onClick={() => onSelect(item.applicationId)}
              >
                <div className="rc-case__top">
                  <strong className="rc-case__name">
                    {item.firstName?.trim() || "Unnamed"} · {item.email || "no email"}
                  </strong>
                  <span className={`rc-badge rc-badge--${item.status}`}>{item.status}</span>
                </div>
                <div className="rc-case__meta">
                  <span>{formatWhen(item.submittedAt)}</span>
                  <span>
                    ID {item.mediaSummary.hasId ? "✓" : "—"} · Video{" "}
                    {item.mediaSummary.hasVideo ? "✓" : "—"} · Photos{" "}
                    {item.mediaSummary.photoCount}
                  </span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
